import { emptyEldvaktApi } from "src/common/service/eldvakt/emptyEldvaktApi.ts";
import { eldvaktApiUrl } from "src/env.ts";
import type {
	AiConversation,
	AiMessage,
	CreateConversationRequest,
	MessageFeedback,
	SearchConversationResult,
	SearchConversationsResponse,
	SubmitFeedbackRequest,
	UpdateConversationRequest,
	UploadFileResponse,
} from "./types";
import type {
	VisualizationChartData,
	VisualizationConfig,
} from "../utils/visualizationUtils";

export const AI_CHAT_ORIGIN = "portal" as const;

export interface AiOptInStatus {
	optedIn: boolean;
	optedInAt: string | null;
}

export type AiSurveyQuestion =
	| { id: string; type: "text" }
	| { id: string; type: "scale"; min: number; max: number };

export type AiPendingNotification =
	| {
			kind: "survey";
			surveyKey: string;
			surveyVersion: number;
			questions: AiSurveyQuestion[];
	  }
	| {
			kind: "optin-reminder";
			surveyKey: string;
			surveyVersion: number;
	  };

export interface AiPendingNotificationsResponse {
	notifications: AiPendingNotification[];
}

export interface AiUserSettings {
	personalPreferences: string | null;
	showThinkingBlocks: boolean;
	showToolActivity: boolean;
	onboardingCompletedAt: string | null;
}

export interface AiCompanyDomainSettings {
	companyPreferences: string | null;
}

export interface AiMemoryItem {
	path: string;
	sizeBytes: number;
	updatedAt: string;
}

export interface AiMemoryListResponse {
	items: AiMemoryItem[];
	totalSizeBytes: number;
}

export interface AiMemoryDeleteResponse {
	deletedCount: number;
}

export const aiChatApi = emptyEldvaktApi.injectEndpoints({
	endpoints: (builder) => ({
		// Get all conversations for a company domain
		getConversations: builder.query<AiConversation[], string>({
			query: (companyDomainId) =>
				`ai/conversations?companyDomainId=${companyDomainId}&origin=${AI_CHAT_ORIGIN}`,
			providesTags: (result) =>
				result
					? [
							"AiConversation",
							...result.map((c) => ({
								type: "AiConversation" as const,
								id: c.id,
							})),
						]
					: ["AiConversation"],
		}),

		// Search conversations
		searchConversations: builder.query<
			SearchConversationsResponse,
			{ companyDomainId: string; q: string; limit?: number; offset?: number }
		>({
			query: ({ companyDomainId, q, limit, offset }) => {
				const params = new URLSearchParams({
					companyDomainId,
					q,
					origin: AI_CHAT_ORIGIN,
				});
				if (limit !== undefined) params.set("limit", String(limit));
				if (offset !== undefined) params.set("offset", String(offset));
				return `ai/conversations/search?${params.toString()}`;
			},
			transformResponse: (response: SearchConversationResult[]) => ({
				results: response,
				total: response.length,
			}),
			providesTags: ["AiConversation"],
		}),

		// Get single conversation with messages
		getConversation: builder.query<
			{ conversation: AiConversation; messages: AiMessage[] },
			{ companyDomainId: string; conversationId: string }
		>({
			query: ({ companyDomainId, conversationId }) =>
				`ai/conversations/${conversationId}?companyDomainId=${companyDomainId}&origin=${AI_CHAT_ORIGIN}`,
			providesTags: (result, _error, arg) => [
				"AiConversation",
				{ type: "AiConversation", id: arg.conversationId },
				"AiMessage",
				...(result?.messages.map((m) => ({
					type: "AiMessage" as const,
					id: m.id,
				})) ?? []),
			],
			transformResponse: (
				response: AiConversation & { messages: AiMessage[] },
			) => ({
				conversation: response,
				messages: response.messages,
			}),
		}),

		// Create new conversation
		createConversation: builder.mutation<
			AiConversation,
			CreateConversationRequest
		>({
			query: ({ companyDomainId, ...body }) => ({
				url: `ai/conversations?companyDomainId=${companyDomainId}`,
				method: "POST",
				body: { ...body, companyDomainId, origin: AI_CHAT_ORIGIN },
			}),
			invalidatesTags: ["AiConversation"],
		}),

		// Update conversation settings
		updateConversation: builder.mutation<
			AiConversation,
			{
				companyDomainId: string;
				conversationId: string;
			} & UpdateConversationRequest
		>({
			query: ({ companyDomainId, conversationId, ...body }) => ({
				url: `ai/conversations/${conversationId}?companyDomainId=${companyDomainId}`,
				method: "PUT",
				body,
			}),
			async onQueryStarted(
				{ conversationId, ...patch },
				{ dispatch, queryFulfilled },
			) {
				const patchConversation = dispatch(
					aiChatApi.util.updateQueryData(
						"getConversation",
						{ conversationId, companyDomainId: patch.companyDomainId },
						(draft) => {
							Object.assign(draft.conversation, patch);
							if (patch.pinned !== undefined) {
								draft.conversation.pinnedAt = patch.pinned
									? new Date().toISOString()
									: null;
							}
						},
					),
				);
				const patchList = dispatch(
					aiChatApi.util.updateQueryData(
						"getConversations",
						patch.companyDomainId,
						(draft) => {
							const conversation = draft.find((c) => c.id === conversationId);
							if (conversation) {
								if (patch.title !== undefined) {
									conversation.title = patch.title;
								}
								if (patch.pinned !== undefined) {
									conversation.pinnedAt = patch.pinned
										? new Date().toISOString()
										: null;
								}
							}
							draft.sort((a, b) => {
								if (a.pinnedAt && !b.pinnedAt) return -1;
								if (!a.pinnedAt && b.pinnedAt) return 1;
								if (a.pinnedAt && b.pinnedAt) {
									return b.pinnedAt.localeCompare(a.pinnedAt);
								}
								return 0;
							});
						},
					),
				);
				try {
					await queryFulfilled;
				} catch {
					patchConversation.undo();
					patchList.undo();
				}
			},
			invalidatesTags: (_result, _error, { conversationId }) => [
				{ type: "AiConversation", id: conversationId },
				"AiConversation",
			],
		}),

		// Delete conversation
		deleteConversation: builder.mutation<
			void,
			{ companyDomainId: string; conversationId: string }
		>({
			query: ({ companyDomainId, conversationId }) => ({
				url: `ai/conversations/${conversationId}?companyDomainId=${companyDomainId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, { conversationId }) => [
				{ type: "AiConversation", id: conversationId },
				"AiConversation",
			],
		}),

		// Delete a message (and all subsequent messages)
		deleteMessage: builder.mutation<
			void,
			{ companyDomainId: string; conversationId: string; messageId: string }
		>({
			query: ({ companyDomainId, conversationId, messageId }) => ({
				url: `ai/conversations/${conversationId}/messages/${messageId}?companyDomainId=${companyDomainId}`,
				method: "DELETE",
			}),
		}),

		// Submit feedback for a message (upsert)
		submitMessageFeedback: builder.mutation<
			MessageFeedback,
			SubmitFeedbackRequest
		>({
			query: ({ companyDomainId, conversationId, messageId, ...body }) => ({
				url: `ai/conversations/${conversationId}/messages/${messageId}/feedback?companyDomainId=${companyDomainId}`,
				method: "PUT",
				body,
			}),
		}),

		// Get user settings for AI chat
		getUserSettings: builder.query<AiUserSettings, string>({
			query: (companyDomainId) =>
				`ai/user-settings?companyDomainId=${companyDomainId}`,
			providesTags: ["AiUserSettings"],
		}),

		// Update user settings for AI chat
		updateUserSettings: builder.mutation<
			AiUserSettings,
			{ companyDomainId: string } & Partial<AiUserSettings>
		>({
			query: ({ companyDomainId, ...body }) => ({
				url: `ai/user-settings?companyDomainId=${companyDomainId}`,
				method: "PUT",
				body,
			}),
			async onQueryStarted(
				{ companyDomainId, ...patch },
				{ dispatch, queryFulfilled },
			) {
				const patchResult = dispatch(
					aiChatApi.util.updateQueryData(
						"getUserSettings",
						companyDomainId,
						(draft) => {
							Object.assign(draft, patch);
						},
					),
				);
				try {
					await queryFulfilled;
				} catch {
					patchResult.undo();
				}
			},
			invalidatesTags: ["AiUserSettings"],
		}),

		// Get company domain settings for AI chat
		getCompanyDomainSettings: builder.query<AiCompanyDomainSettings, string>({
			query: (companyDomainId) =>
				`ai/company-domain-settings?companyDomainId=${companyDomainId}`,
			providesTags: ["AiCompanyDomainSettings"],
		}),

		// Update company domain settings for AI chat
		updateCompanyDomainSettings: builder.mutation<
			AiCompanyDomainSettings,
			{ companyDomainId: string } & Partial<AiCompanyDomainSettings>
		>({
			query: ({ companyDomainId, ...body }) => ({
				url: `ai/company-domain-settings?companyDomainId=${companyDomainId}`,
				method: "PUT",
				body,
			}),
			async onQueryStarted(
				{ companyDomainId, ...patch },
				{ dispatch, queryFulfilled },
			) {
				const patchResult = dispatch(
					aiChatApi.util.updateQueryData(
						"getCompanyDomainSettings",
						companyDomainId,
						(draft) => {
							Object.assign(draft, patch);
						},
					),
				);
				try {
					await queryFulfilled;
				} catch {
					patchResult.undo();
				}
			},
			invalidatesTags: ["AiCompanyDomainSettings"],
		}),

		// Get current user's AI opt-in status for a company domain
		getAiOptIn: builder.query<AiOptInStatus, string>({
			query: (companyDomainId) =>
				`ai/user-opt-in?companyDomainId=${encodeURIComponent(companyDomainId)}`,
			providesTags: (_result, _error, companyDomainId) => [
				"AiUserOptIn",
				{ type: "AiUserOptIn", id: companyDomainId },
			],
		}),

		// Opt current user in to AI
		optInToAi: builder.mutation<{ optedInAt: string }, string>({
			query: (companyDomainId) => ({
				url: "ai/user-opt-in",
				method: "POST",
				body: { companyDomainId },
			}),
			invalidatesTags: (_result, _error, companyDomainId) => [
				"AiUserOptIn",
				{ type: "AiUserOptIn", id: companyDomainId },
				"AiPendingNotifications",
			],
		}),

		// Get pending in-portal notifications (survey / reminder) for the user
		getAiPendingNotifications: builder.query<
			AiPendingNotificationsResponse,
			string
		>({
			query: (companyDomainId) =>
				`ai/user-notifications/pending?companyDomainId=${encodeURIComponent(companyDomainId)}`,
			providesTags: ["AiPendingNotifications"],
		}),

		// Submit a survey response (partial submissions allowed)
		submitAiSurveyResponse: builder.mutation<
			void,
			{
				companyDomainId: string;
				surveyKey: string;
				surveyVersion: number;
				answers: Record<string, string | number>;
			}
		>({
			query: ({ surveyKey, surveyVersion, ...body }) => ({
				url: `ai/surveys/${encodeURIComponent(surveyKey)}/${surveyVersion}/responses`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["AiPendingNotifications"],
		}),

		// Dismiss a pending survey or reminder (snooze 14d or permanent)
		dismissAiNotification: builder.mutation<
			void,
			{
				companyDomainId: string;
				surveyKey: string;
				surveyVersion: number;
				mode: "snooze" | "permanent";
			}
		>({
			query: ({ surveyKey, surveyVersion, ...body }) => ({
				url: `ai/user-notifications/${encodeURIComponent(surveyKey)}/${surveyVersion}/dismiss`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["AiPendingNotifications"],
		}),

		// Upload a file (staged until connected to a conversation via fileIds)
		uploadFile: builder.mutation<
			UploadFileResponse,
			{ companyDomainId: string; file: File }
		>({
			query: ({ companyDomainId, file }) => {
				const formData = new FormData();
				formData.append("companyDomainId", companyDomainId);
				formData.append("file", file);
				return {
					url: "ai/files/upload",
					method: "POST",
					body: formData,
				};
			},
		}),

		// Get chart data for a visualization
		getVisualizationChartData: builder.query<
			VisualizationChartData,
			{ companyDomainId: string; config: VisualizationConfig }
		>({
			query: ({ companyDomainId, config }) => {
				const params = new URLSearchParams({
					companyDomainId,
					xField: config.xAxis.field,
					yField: config.yAxis.field,
					chartType: config.chartType,
					aggregation: config.aggregation,
					sort: config.sort,
				});
				if (config.groupField) {
					params.set("groupField", config.groupField);
				}
				if (config.topN) {
					params.set("topNEnabled", String(config.topN.enabled));
					params.set("topNLimit", String(config.topN.limit));
					params.set("topNOtherLabel", config.topN.otherLabel);
				}
				return `ai/cache/${config.cacheId}/chart?${params.toString()}`;
			},
		}),

		// List memory files for the calling user in this company
		getMemoryItems: builder.query<AiMemoryListResponse, string>({
			query: (companyDomainId) =>
				`ai/memory?companyDomainId=${encodeURIComponent(companyDomainId)}`,
			providesTags: (_result, _error, companyDomainId) => [
				"AiMemory",
				{ type: "AiMemory", id: companyDomainId },
			],
		}),

		// Wipe all memory files for the calling user in this company
		clearAllMemory: builder.mutation<AiMemoryDeleteResponse, string>({
			query: (companyDomainId) => ({
				url: `ai/memory?companyDomainId=${encodeURIComponent(companyDomainId)}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, companyDomainId) => [
				"AiMemory",
				{ type: "AiMemory", id: companyDomainId },
			],
		}),

		// Delete a single memory file (optimistic update on the cached list)
		deleteMemoryFile: builder.mutation<
			AiMemoryDeleteResponse,
			{ companyDomainId: string; path: string }
		>({
			query: ({ companyDomainId, path }) => ({
				url: `ai/memory/file?companyDomainId=${encodeURIComponent(companyDomainId)}&path=${encodeURIComponent(path)}`,
				method: "DELETE",
			}),
			async onQueryStarted(
				{ companyDomainId, path },
				{ dispatch, queryFulfilled },
			) {
				const patch = dispatch(
					aiChatApi.util.updateQueryData(
						"getMemoryItems",
						companyDomainId,
						(draft) => {
							const idx = draft.items.findIndex((item) => item.path === path);
							if (idx !== -1) {
								draft.totalSizeBytes -= draft.items[idx].sizeBytes;
								draft.items.splice(idx, 1);
							}
						},
					),
				);
				try {
					await queryFulfilled;
				} catch {
					patch.undo();
				}
			},
			invalidatesTags: (_result, _error, { companyDomainId }) => [
				"AiMemory",
				{ type: "AiMemory", id: companyDomainId },
			],
		}),
	}),
});

export const {
	useSearchConversationsQuery,
	useGetConversationsQuery,
	useGetConversationQuery,
	useCreateConversationMutation,
	useUpdateConversationMutation,
	useDeleteConversationMutation,
	useDeleteMessageMutation,
	useSubmitMessageFeedbackMutation,
	useUploadFileMutation,
	useGetUserSettingsQuery,
	useUpdateUserSettingsMutation,
	useGetCompanyDomainSettingsQuery,
	useUpdateCompanyDomainSettingsMutation,
	useGetAiOptInQuery,
	useOptInToAiMutation,
	useGetAiPendingNotificationsQuery,
	useSubmitAiSurveyResponseMutation,
	useDismissAiNotificationMutation,
	useGetVisualizationChartDataQuery,
	useGetMemoryItemsQuery,
	useClearAllMemoryMutation,
	useDeleteMemoryFileMutation,
} = aiChatApi;

export function getFileThumbnailUrl(
	fileId: string,
	companyDomainId: string,
): string {
	return `${eldvaktApiUrl}/ai/files/${fileId}/thumbnail?companyDomainId=${encodeURIComponent(companyDomainId)}`;
}

export function getFileDownloadUrl(
	fileId: string,
	companyDomainId: string,
): string {
	return `${eldvaktApiUrl}/ai/files/${fileId}/download?companyDomainId=${encodeURIComponent(companyDomainId)}`;
}

export function getContainerFileDownloadUrl(
	fileId: string,
	companyDomainId: string,
): string {
	return `${eldvaktApiUrl}/ai/files/container/${fileId}/download?companyDomainId=${encodeURIComponent(companyDomainId)}`;
}
